// How dates and times are written and read in the language chosen in DoCuLat (TF-92), not the
// browser's: Latvian is dd.mm.yyyy, 24-hour, weeks from Monday; English is British English, as the
// app's calendars already write it (dd/mm/yyyy, 24-hour, weeks from Monday). The rest follows from the
// locale's tag, so another locale (a 12-hour clock, weeks from Sunday) needs only its tag. Pure, so it
// is tested alone; the fields are dates.tsx, the calendar is calendar.tsx.
//
// Values are what the API and the native inputs used: "2026-10-05" for a day, "14:30" for a time,
// "2026-10-05T14:30" for both, in local time.

import { isLanguage, languageEntry } from '@smbsuite/i18n';

export type Part = 'd' | 'm' | 'y';

export interface DateLocale {
  /** The BCP 47 tag the numbers and names are written in ("lv", "en-GB", "en-US"). */
  tag: string;
  /** The order of day, month and year when written as numbers. */
  order: Part[];
  separator: string;
  /** 0 Sunday ... 6 Saturday. */
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  hour12: boolean;
}

const cache = new Map<string, DateLocale>();

/** The first day of the week a locale uses; Intl knows it where the browser supports weekInfo. */
function firstDay(tag: string): DateLocale['weekStartsOn'] {
  try {
    const locale = new Intl.Locale(tag) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    const day = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay;
    if (day) return (day % 7) as DateLocale['weekStartsOn'];
  } catch {
    // an old browser: the fallback below
  }
  return /-(US|CA|JP|IL|PH|BR|MX)$/i.test(tag) ? 0 : 1;
}

/** The locale of a language of the app ("lv", "en": its entry's tag for dates), or of a tag ("en-US"). */
export function dateLocale(language: string): DateLocale {
  const tag = isLanguage(language) ? languageEntry(language).dates : language;
  const known = cache.get(tag);
  if (known) return known;
  const parts = new Intl.DateTimeFormat(tag, { day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(new Date(2026, 10, 25));
  const order = parts.flatMap((part) => (part.type === 'day' ? ['d'] : part.type === 'month' ? ['m'] : part.type === 'year' ? ['y'] : [])) as Part[];
  const separator = parts.find((part) => part.type === 'literal')?.value.trim() || '.';
  const hour12 = new Intl.DateTimeFormat(tag, { hour: 'numeric' }).resolvedOptions().hour12 === true;
  const locale: DateLocale = { tag, order: order.length === 3 ? order : ['d', 'm', 'y'], separator, weekStartsOn: firstDay(tag), hour12 };
  cache.set(tag, locale);
  return locale;
}

const pad = (value: number) => String(value).padStart(2, '0');
const isoDay = (year: number, month: number, day: number) => `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;

/** A real day? (31 April is not.) */
function exists(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** "2026-10-05" written as the locale writes it: "05.10.2026", "05/10/2026", "10/05/2026". */
export function formatDate(value: string, locale: DateLocale): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return '';
  const parts = { y: match[1]!, m: match[2]!, d: match[3]! };
  return locale.order.map((part) => parts[part]).join(locale.separator);
}

/** What to type, in the language's letters: "dd.mm.gggg". */
export function datePattern(locale: DateLocale, letters: Record<Part, string>): string {
  return locale.order.map((part) => letters[part]).join(locale.separator);
}

/**
 * A typed day: "" when nothing is typed, null when it is not a day. Takes the locale's order with
 * any separator ("5.10.2026", "5/10/26", "05102026"), and the ISO form ("2026-10-05", the start of a
 * pasted "2026-10-05T14:30" too). A two-digit year is in this century.
 */
export function parseDate(text: string, locale: DateLocale): string | null {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]\d{1,2}:\d{2}.*)?$/.exec(trimmed);
  if (iso) {
    const [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    return exists(year, month, day) ? isoDay(year, month, day) : null;
  }
  let numbers = trimmed.split(/[^\d]+/).filter(Boolean);
  // Written without separators: two digits each for day and month, the rest the year.
  if (numbers.length === 1 && (numbers[0]!.length === 6 || numbers[0]!.length === 8)) {
    const digits = numbers[0]!;
    numbers = [];
    let at = 0;
    for (const part of locale.order) {
      const length = part === 'y' ? digits.length - 4 : 2;
      numbers.push(digits.slice(at, at + length));
      at += length;
    }
  }
  if (numbers.length !== 3) return null;
  const given: Record<Part, number> = { d: 0, m: 0, y: 0 };
  locale.order.forEach((part, index) => {
    given[part] = Number(numbers[index]);
  });
  const yearText = numbers[locale.order.indexOf('y')]!;
  if (yearText.length === 2) given.y += 2000;
  else if (yearText.length !== 4) return null;
  return exists(given.y, given.m, given.d) ? isoDay(given.y, given.m, given.d) : null;
}

/** A day and a month without a year (a birthday): "05.10" in Latvian, "10/05" in American English. */
export function formatDayMonth(day: string, month: string, locale: DateLocale): string {
  if (!day || !month) return '';
  const parts = { d: pad(Number(day)), m: pad(Number(month)) };
  return locale.order.filter((part) => part !== 'y').map((part) => parts[part as 'd' | 'm']).join(locale.separator);
}

/** A typed day and month: null when they are not one (29 February is). */
export function parseDayMonth(text: string, locale: DateLocale): { day: string; month: string } | null {
  const numbers = text.trim().split(/[^\d]+/).filter(Boolean);
  if (numbers.length === 0) return { day: '', month: '' };
  if (numbers.length !== 2) return null;
  const order = locale.order.filter((part) => part !== 'y');
  const day = Number(numbers[order.indexOf('d')]);
  const month = Number(numbers[order.indexOf('m')]);
  // 2000 had a 29 February.
  return exists(2000, month, day) ? { day: String(day), month: String(month) } : null;
}

/** "14:30" as the locale shows its hour: "14" or "2" with "PM". */
export function timeParts(value: string, locale: DateLocale): { hours: string; minutes: string; pm: boolean } {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return { hours: '', minutes: '', pm: false };
  const hours = Number(match[1]);
  if (!locale.hour12) return { hours: pad(hours), minutes: match[2]!, pm: hours >= 12 };
  return { hours: String(hours % 12 === 0 ? 12 : hours % 12), minutes: match[2]!, pm: hours >= 12 };
}

/** The time the boxes say: "" when both are empty, null when they are not a time. */
export function joinTime(hours: string, minutes: string, pm: boolean, locale: DateLocale): string | null {
  if (!hours.trim() && !minutes.trim()) return '';
  if (!/^\d{1,2}$/.test(hours.trim()) || !/^\d{1,2}$/.test(minutes.trim() || '0')) return null;
  let hour = Number(hours);
  const minute = Number(minutes.trim() || '0');
  if (minute > 59) return null;
  if (locale.hour12) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (pm ? 12 : 0);
  } else if (hour > 23) return null;
  return `${pad(hour)}:${pad(minute)}`;
}

/** "2026-10-05T14:30" -> the day and the time; either may be "". */
export function splitDateTime(value: string): { date: string; time: string } {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/.exec(value);
  return match ? { date: match[1]!, time: match[2] ?? '' } : { date: '', time: '' };
}

/** A Date for a day ("2026-10-05"), at noon local time: safe from summer time either way. */
export function dayToDate(value: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12) : undefined;
}

/** The day of a Date, as "2026-10-05" in local time. */
export function dateToDay(date: Date): string {
  return isoDay(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Today, as "2026-10-05" in local time. */
export const today = () => dateToDay(new Date());
